/**
 * Pure i18n logic: dictionaries, language list, detection, lookup.
 *
 * Deliberately free of JSX so it can be imported directly by tests. The React
 * provider lives in ./index.js, which re-exports everything here — app code
 * should keep importing from '../i18n'.
 */
import en from './locales/en';
import it from './locales/it';
import es from './locales/es';
import fr from './locales/fr';
import de from './locales/de';
import pt from './locales/pt';
import nl from './locales/nl';

// English is the fallback for any key a translation has not covered yet, so a
// missing string degrades to English rather than to the raw key.
export const LOCALES = { en, it, es, fr, de, pt, nl };

export const LANGUAGES = [
  { code: 'en', label: 'English', endonym: 'English' },
  { code: 'it', label: 'Italian', endonym: 'Italiano' },
  { code: 'es', label: 'Spanish', endonym: 'Español' },
  { code: 'fr', label: 'French', endonym: 'Français' },
  { code: 'de', label: 'German', endonym: 'Deutsch' },
  { code: 'pt', label: 'Portuguese', endonym: 'Português' },
  { code: 'nl', label: 'Dutch', endonym: 'Nederlands' },
];

export const STORAGE_KEY = 'aics.lang';
export const DEFAULT_LANG = 'en';

export const isSupported = (code) => Object.prototype.hasOwnProperty.call(LOCALES, code);

const readStored = () => {
  // Storage throws in private mode and in some embedded webviews.
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && isSupported(v) ? v : null;
  } catch {
    return null;
  }
};

export const writeStored = (code) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // A viewer who cannot persist still gets the right language this visit.
  }
};

/**
 * First visit: follow the browser. navigator.languages is ordered by the
 * person's own preference, so the first entry we actually support wins -
 * someone with ['fr-CA', 'en'] gets French, not English. Region is dropped
 * because we translate per language, not per locale.
 * Afterwards an explicit choice always wins, and is never overridden.
 */
export const detectLanguage = () => {
  const stored = readStored();
  if (stored) return stored;

  const prefs =
    (typeof navigator !== 'undefined' &&
      (navigator.languages || (navigator.language ? [navigator.language] : []))) ||
    [];

  for (const pref of prefs) {
    const base = String(pref).toLowerCase().split('-')[0];
    if (isSupported(base)) return base;
  }
  return DEFAULT_LANG;
};

export const translate = (lang, key, vars) => {
  const dict = LOCALES[lang] || LOCALES[DEFAULT_LANG];
  let str = dict[key];
  if (str === undefined) str = LOCALES[DEFAULT_LANG][key];
  if (str === undefined) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[i18n] missing key: ${key}`);
    }
    return key;
  }
  if (vars) {
    Object.keys(vars).forEach((k) => {
      str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
    });
  }
  return str;
};
